import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, RefreshCw, Eye, Calendar } from "lucide-react";
import { format } from "date-fns";

const BlogManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: posts, isLoading } = useQuery({
    queryKey: ["admin-blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .order("published_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    },
  });

  const generateBlogPost = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("generate-blog-post");
      
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Success!",
        description: `Blog post "${data.post.title}" has been generated and published`,
      });
      queryClient.invalidateQueries({ queryKey: ["admin-blog-posts"] });
      setIsGenerating(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate blog post",
        variant: "destructive",
      });
      setIsGenerating(false);
    },
  });

  const handleGeneratePost = async () => {
    setIsGenerating(true);
    generateBlogPost.mutate();
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold">Blog Management</h1>
            <p className="text-muted-foreground mt-2">
              Automatically generate trending blog posts using AI
            </p>
          </div>
          
          <Button
            onClick={handleGeneratePost}
            disabled={isGenerating}
            size="lg"
            className="gap-2"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="h-5 w-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" />
                Generate New Blog Post
              </>
            )}
          </Button>
        </div>

        <Card className="p-6 bg-primary/5">
          <h3 className="font-semibold mb-2">🤖 Automated Blog System</h3>
          <p className="text-sm text-muted-foreground">
            This system uses AI to automatically generate SEO-optimized blog posts about trending digital marketing topics.
            Each post is unique, informative, and tailored to your agency's expertise.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            💡 <strong>Tip:</strong> You can set up a cron job to automatically generate posts daily or weekly.
          </p>
        </Card>
      </div>

      {/* Blog Posts List */}
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold">Recent Posts</h2>
        
        {isLoading ? (
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6 animate-pulse">
                <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </Card>
            ))}
          </div>
        ) : posts && posts.length > 0 ? (
          <div className="grid gap-4">
            {posts.map((post) => (
              <Card key={post.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Badge variant={post.status === "published" ? "default" : "secondary"}>
                        {post.status}
                      </Badge>
                      <Badge variant="outline">{post.category}</Badge>
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(post.published_at), "MMM dd, yyyy")}
                      </span>
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {post.view_count || 0} views
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-semibold mb-2">{post.title}</h3>
                    <p className="text-muted-foreground line-clamp-2">{post.excerpt}</p>
                    
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {post.tags.slice(0, 5).map((tag: string, index: number) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={() => window.open(`/blog/${post.slug}`, "_blank")}
                  >
                    View Post
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">
              No blog posts yet. Click "Generate New Blog Post" to create your first post!
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BlogManagement;
